import React from 'react';
import { toast } from 'react-toastify';
import metadata from '../../metadata.json';

const MainForm = ({
  formData,
  errors,
  loading,
  emailStatus,
  forceVerifyButton,
  initiatedPayment,
  translations,
  onSubmit,
  onChange,
  onCheckEmail,
  onEmailChange,
  onEmailBlur,
  isFormValid,
  scanType,
  handleForgetLicenseKey,
  checkMailError,
  licenseKeyInvalid,
  checkingEmail,
  buttonColor = '#2563eb',
  backgroundColor = '#ffffff',
  dbVersion,
}) => {
  const isEmailDomainMatching = () => {
    if (!formData.email || !formData.target) return false;

    const emailDomain = formData.email.split('@')[1]?.toLowerCase();
    const targetDomain = formData.target.toLowerCase().replace(/^www\./, '');
    const normalizedEmailDomain = emailDomain?.replace(/^www\./, '');
    return normalizedEmailDomain === targetDomain;
  };

  const getCurrentVersion = () => {
    const now = new Date();
    const buildRevision = metadata.buildRevision + Math.floor(now.getTime() / 1000000) % 100;
    return {
      buildMajor: metadata.buildMajor,
      buildMinor: metadata.buildMinor,
      buildRevision: buildRevision,
    };
  };

  const lightenColor = (color, percent = 80) => {
    const num = parseInt(color.replace('#', ''), 16);

    let r = (num >> 16) + Math.round(((255 - (num >> 16)) * percent) / 100);
    let g = ((num >> 8) & 0x00ff) + Math.round(((255 - ((num >> 8) & 0x00ff)) * percent) / 100);
    let b = (num & 0x0000ff) + Math.round(((255 - (num & 0x0000ff)) * percent) / 100);

    return `rgb(${r}, ${g}, ${b})`;
  };

  const containerStyle = {
    maxWidth: '1200px',
    backgroundColor: lightenColor(backgroundColor, 85) || '#ffffff',
    transition: 'background-color 0.3s ease',
    borderRadius: '1rem',
  };

  const buttonStyle = {
    background: buttonColor,
    opacity:
      !isFormValid() ||
      emailStatus === 'rate_limited' ||
      emailStatus === 'org_conflict' ||
      licenseKeyInvalid ||
      checkingEmail
        ? 0.7
        : 1,
    transition: 'background-color 0.3s ease, transform 0.2s ease',
    border: 'none',
    width: '100%',
    color: 'white',
    padding: '0.75rem 1.25rem',
    borderRadius: '1rem',
    fontWeight: '700',
  };

  return (
    <div className="mx-auto shadow p-4 rounded-4" style={containerStyle}>
      <div className="text-center">
        <h4 className="fw-bold">{translations.formTitle}</h4>
        <p className="text-muted mb-0 mb-lg-3">{translations.formDescription}</p>
      </div>

      <form onSubmit={onSubmit} noValidate>
        <div className="row">
          <div className="col-md-6 mb-1 mb-lg-3 mb-md-0">
            <label className="form-label">{translations.firstNameLabel}</label>
            <input
              type="text"
              className={`form-control py-2 ${errors.firstName ? 'is-invalid' : ''}`}
              name="firstName"
              value={formData.firstName}
              onChange={onChange}
              placeholder={translations.firstNamePlaceholder}
              style={{ boxShadow: 'none' }}
            />
            {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
          </div>
          <div className="col-md-6">
            <label className="form-label">{translations.lastNameLabel}</label>
            <input
              type="text"
              className={`form-control py-2 ${errors.lastName ? 'is-invalid' : ''}`}
              name="lastName"
              value={formData.lastName}
              onChange={onChange}
              placeholder={translations.lastNamePlaceholder}
              style={{ boxShadow: 'none' }}
            />
            {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
          </div>
        </div>

        <div className="mb-1 mb-lg-3 position-relative">
          <label className="form-label">
            {translations.emailLabel}&nbsp;
            <span className="text-muted small">{translations.emailDescription}</span>
          </label>
          <input
            type="email"
            className={`form-control py-2 pe-5 ${errors.email ? 'is-invalid' : ''}`}
            name="email"
            value={formData.email}
            onChange={onEmailChange || onChange}
            onAnimationStart={(e) => {
              if (
                e.animationName.includes('auto-fill') ||
                e.animationName.includes('onAutoFillStart')
              ) {
                onCheckEmail({ target: { value: formData.email } });
              }
            }}
            onInput={(e) => {
              if (e.target.value && e.target.value !== formData.email) {
                if (onEmailChange) onEmailChange(e);
              }
            }}
            placeholder={translations.emailPlaceholder}
            style={{ boxShadow: 'none' }}
          />
          {emailStatus === 'verified' && (
            <div
              className="position-absolute verified_icons end-0 d-flex align-items-center me-2 text-success"
              style={{ fontSize: '12px' }}
            >
              <span className="me-1">✔</span> {translations.emailVerified}
            </div>
          )}
          {checkingEmail && (
            <div
              className="position-absolute verified_icons end-0 d-flex align-items-center me-2 text-secondary"
              style={{ fontSize: '12px' }}
            >
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                style={{ width: '12px', height: '12px', borderWidth: '2px' }}
              />
              Checking...
            </div>
          )}
          {emailStatus === 'unverified' && !checkingEmail && (
            <div
              className="position-absolute verified_icons end-0 d-flex align-items-center me-2 text-danger"
              style={{ fontSize: '12px' }}
            >
              <span className="me-1">✖</span> {translations.emailNotVerified}
            </div>
          )}

          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
          {checkMailError && <div className="text-danger small mt-1">{checkMailError}</div>}
        </div>

        <div className="mb-3">
          <label className="form-label">{translations.domainLabel}</label>
          <div className="input-group">
            <span className="input-group-text">https://</span>
            <input
              type="text"
              className={`form-control py-2 ${errors.target ? 'is-invalid' : ''}`}
              name="target"
              value={formData.target}
              onChange={onChange}
              placeholder={translations.domainPlaceholder}
              style={{ boxShadow: 'none' }}
            />
          </div>
          {errors.target && <div className="invalid-feedback">{errors.target}</div>}
          <small className="text-muted d-block mt-1 domain_name">{translations.domainDescription}</small>
          {formData.email && formData.target && (
            <div className={`mt-1 small ${isEmailDomainMatching() ? 'text-success' : 'text-warning'}`}>
              {isEmailDomainMatching() ? (
                <><span className="me-1">✔</span> Email domain matches target domain</>
              ) : (
                <><span className="me-1">⚠</span> Email domain does not match target domain</>
              )}
            </div>
          )}
        </div>

        {scanType === 'license' && emailStatus === 'verified' && (
          <div className="mb-1 mb-lg-3">
            <label htmlFor="licenseKey" className="form-label">
              License Key <span className='text-danger'>*</span>
            </label>
            <input
              type="text"
              className={`form-control py-2 ${errors.licenseId ? 'is-invalid' : ''}`}
              id="licenseKey"
              name="licenseId"
              value={formData.licenseId}
              onChange={onChange}
              placeholder="Enter License key"
              style={{ boxShadow: 'none' }}
            />
            {errors.licenseId && <div className="invalid-feedback">{errors.licenseId}</div>}
            <div
              className='d-flex justify-content-end align-items-end'
              onClick={() => {
                if (handleForgetLicenseKey) {
                  handleForgetLicenseKey();
                }
              }}
            >
              <a className='text-primary' style={{ cursor: 'pointer' }}>
                Forgot License Key
              </a>
            </div>
          </div>
        )}

        <div className="form-check d-flex align-items-center mb-3">
          <input
            className={`form-check-input ${errors.agree ? 'is-invalid' : ''}`}
            type="checkbox"
            name="agree"
            checked={formData.agree}
            onChange={onChange}
            id="tos"
            style={{ boxShadow: 'none' }}
          />
          <label className="form-check-label ms-2 terms-label" htmlFor="tos">
            {translations.termsAgreement}{' '}
            <a href="https://riskreport360.com/terms" target="_blank" rel="noopener noreferrer">
              {translations.termsOfService}
            </a>{' '}
            {translations.termsAgreementEnd}
          </label>
          {errors.agree && <div className="invalid-feedback d-block">{errors.agree}</div>}
        </div>

        <button
          type="submit"
          className="btn w-100 text-white px-3 py-2 rounded-4 fw-bold"
          style={buttonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.filter = 'brightness(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.filter = 'brightness(1)';
          }}
          disabled={
            loading ||
            !isFormValid() ||
            emailStatus === 'rate_limited' ||
            emailStatus === 'org_conflict' ||
            licenseKeyInvalid ||
            checkingEmail
          }
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              {translations.submitting}
            </>
          ) : forceVerifyButton ? (
            <>
              <i className="bi bi-envelope-exclamation-fill me-2" />
              {translations.verifyEmail}
            </>
          ) : emailStatus === 'verified' && !initiatedPayment ? (
            <>
              <i className={scanType === 'license' ? 'bi bi-file-earmark-text-fill me-2' : 'bi bi-credit-card-fill me-2'} />
              {scanType === 'license' ? 'Scan Report' : translations.initializePayment}
            </>
          ) : emailStatus === 'verified' && initiatedPayment ? (
            <>
              <i className="bi bi-shield-lock-fill me-2" />
              {translations.startSecurityAssessment || 'Start Security Assessment'}
            </>
          ) : (
            <>
              <i className="bi bi-shield-lock-fill me-2" />
              {translations.startAssessment}
            </>
          )}
        </button>
      </form>

      <div
        className="alert d-flex justify-content-end align-items-center rounded-2 mt-3 px-3 py-1"
        style={{
          backgroundColor: 'rgba(239, 246, 255, 0.8)',
          border: '1px solid rgb(191 219 254)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <p className="mb-0 version">
          {dbVersion
            ? dbVersion
            : `v${getCurrentVersion().buildMajor}.${getCurrentVersion().buildMinor}.${getCurrentVersion().buildRevision}`}
        </p>
      </div>
    </div>
  );
};

export default MainForm;
